import { useTranslation } from '../../../i18n';
import React, { useEffect, useState } from 'react';
import { AutoComplete, Button, Card, Checkbox, Col, Collapse, Form, Input, InputNumber, Modal, Row, Select, Space, Typography, theme } from 'antd';
import { EditOutlined, KeyOutlined, ReloadOutlined, ThunderboltOutlined } from '@ant-design/icons';
import SecretText from '../../SecretText';
import { generatePassword, ok, runAction } from './utils';

export const CPU_MODELS = ['IvyBridge', 'Haswell-noTSX', 'Broadwell-noTSX', 'Skylake-Server', 'Cascadelake-Server', 'EPYC', 'EPYC-Rome', 'EPYC-Milan', 'host-model', 'host-passthrough'];
export const CPU_MODEL_RE = /^([A-Za-z][A-Za-z0-9_.-]{0,63})?$/;

/** Admin only: CPU model / nested virtualization override of one VM. */
const CpuForm = ({ id, adapter, reload, disabled }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [info, setInfo] = useState(null);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        adapter.getCpu(id).then((r) => {
            if (!ok(r)) return;
            setInfo(r.data);
            form.setFieldsValue({
                cpu_model: r.data.cpu_model || '',
                nested_virt: r.data.nested_virt === null ? '' : String(r.data.nested_virt),
                restart: false,
            });
        });
    }, [adapter, id, form]);
    const save = async (v) => {
        setSaving(true);
        const res = await runAction(() => adapter.setCpu(id, { ...v, restart: v.restart ? 1 : 0 }), t('Saved'), t('Failed to save'));
        setSaving(false);
        if (res) reload();
    };
    if (info && !info.ready) return <Text type="secondary">{t('Database migration pending')}</Text>;
    return (
        <Form form={form} layout="vertical" onFinish={save}>
            <Row gutter={12}>
                <Col xs={24} md={12}>
                    <Form.Item
                        name="cpu_model"
                        label={t('CPU model')}
                        tooltip={t('host-passthrough performs best, but can only migrate between nodes with the same CPU')}
                        extra={info ? `${t('In effect: {{v}}', { v: info.effective_cpu_model })}${Object.values(info.host_models || {})[0] ? ` · ${t('Node CPU: {{v}}', { v: Object.values(info.host_models)[0] })}` : ''}` : null}
                        rules={[{ pattern: CPU_MODEL_RE, message: t('Invalid model') }]}
                    >
                        <AutoComplete allowClear placeholder={t('Follow plan')} options={(info?.reported ? info.models : CPU_MODELS).map((m) => ({ value: m }))} />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item name="nested_virt" label={t('Nested virtualization')}>
                        <Select options={[{ value: '', label: t('Follow plan') }, { value: '1', label: t('Enable') }, { value: '0', label: t('Close') }]} />
                    </Form.Item>
                </Col>
            </Row>
            <Space>
                <Button type="primary" htmlType="submit" loading={saving} disabled={disabled}>{t('Save')}</Button>
                <Form.Item name="restart" valuePropName="checked" noStyle>
                    <Checkbox>{t('Restart now to apply')}</Checkbox>
                </Form.Item>
            </Space>
        </Form>
    );
};

const { Text } = Typography;

const SettingRow = ({ label, value, action, isMobile }) => {
    const { token } = theme.useToken();
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'stretch' : 'center',
                justifyContent: 'space-between',
                gap: 8,
                padding: '12px 0',
                borderBottom: `1px solid ${token.colorBorderSecondary}`,
            }}
        >
            <div style={{ minWidth: 0 }}>
                <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>{label}</Text>
                <div style={{ marginTop: 2 }}>{value}</div>
            </div>
            <div style={{ flexShrink: 0 }}>{action}</div>
        </div>
    );
};

const SettingsTab = ({ id, vm, api, adapter, mode, isMobile, disabled, reload, openRebuild }) => {
    const { t } = useTranslation();
    const isAdmin = mode === 'admin';
    const [modal, setModal] = useState(null); // 'hostname' | 'password' | 'vnc'
    const [submitting, setSubmitting] = useState(false);
    const [newPassword, setNewPassword] = useState(null);
    const [form] = Form.useForm();
    const [configForm] = Form.useForm();

    const open = (kind) => {
        form.resetFields();
        if (kind === 'hostname') form.setFieldsValue({ value: vm.hostname });
        if (kind === 'password') form.setFieldsValue({ value: generatePassword(12) });
        if (kind === 'vnc') form.setFieldsValue({ value: generatePassword(8) });
        setModal(kind);
    };

    const submit = async ({ value }) => {
        setSubmitting(true);
        let res;
        if (modal === 'hostname') res = await runAction(() => api.setHostname(id, value.trim()), t('Hostname changed'), t('Failed to change hostname'));
        if (modal === 'password') res = await runAction(() => api.setPassword(id, value), t('Password reset'), t('Failed to reset password'));
        if (modal === 'vnc' && isAdmin) res = await runAction(() => api.setVncPassword(id, value), t('VNC password changed'), t('Failed to change VNC password'));
        setSubmitting(false);
        if (res) {
            if (modal === 'password') setNewPassword(value);
            setModal(null);
            reload();
        }
    };

    const saveConfig = async (values) => {
        setSubmitting(true);
        const res = await runAction(() => api.updateConfig(id, {
            core: values.core,
            memory: values.memory,
            net_out: values.net_out,
            flow_limit: values.flow_limit,
            restart: values.restart ? 1 : 0,
        }), t('Configuration submitted'), t('Failed to update configuration'));
        setSubmitting(false);
        if (res) reload();
    };

    const loginUser = vm.login_user || 'root';
    const titles = { hostname: t('Change hostname'), password: `${t('Reset password')} · ${loginUser}`, vnc: t('Change VNC password') };

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Card size="small" bodyStyle={{ paddingTop: 0, paddingBottom: 0 }}>
                <SettingRow
                    isMobile={isMobile}
                    label={t('Hostname')}
                    value={<Text>{vm.hostname || '-'}</Text>}
                    action={<Button icon={<EditOutlined />} disabled={disabled} onClick={() => open('hostname')}>{t('Edit')}</Button>}
                />
                <SettingRow
                    isMobile={isMobile}
                    label={t('Username')}
                    value={<Text copyable>{loginUser}</Text>}
                />
                <SettingRow
                    isMobile={isMobile}
                    label={t('Password')}
                    value={<SecretText value={newPassword || vm.passwd} />}
                    action={<Button icon={<KeyOutlined />} disabled={disabled} onClick={() => open('password')}>{t('Reset password')}</Button>}
                />
                {isAdmin && (
                    <SettingRow
                        isMobile={isMobile}
                        label={t('VNC password{{value}}', { value: vm.vnc_port ? t(' · Port {{vnc_port}}', { vnc_port: vm.vnc_port }) : '' })}
                        value={<SecretText value={vm.vnc_passwd} />}
                        action={<Button icon={<EditOutlined />} disabled={disabled} onClick={() => open('vnc')}>{t('Edit')}</Button>}
                    />
                )}
                <div style={{ borderBottom: 'none' }}>
                    <SettingRow
                        isMobile={isMobile}
                        label={t('Operating system')}
                        value={<Text>{vm.image?.name || '-'}</Text>}
                        action={<Button danger icon={<ReloadOutlined />} disabled={disabled} onClick={openRebuild}>{t('Reinstall OS')}</Button>}
                    />
                </div>
            </Card>

            {isAdmin && (
                <Collapse
                    size="small"
                    items={[{
                        key: 'advanced',
                        label: t('Advanced: Configuration adjustments'),
                        children: (
                            <Form
                                form={configForm}
                                layout="vertical"
                                onFinish={saveConfig}
                                initialValues={{ core: vm.core, memory: vm.memory, net_out: vm.net_out, flow_limit: vm.flow_limit, restart: false }}
                            >
                                <Row gutter={12}>
                                    <Col xs={12} md={6}>
                                        <Form.Item name="core" label={t('CPU (cores)')} rules={[{ required: true, message: t('Required') }]}>
                                            <InputNumber min={1} max={64} style={{ width: '100%' }} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={12} md={6}>
                                        <Form.Item name="memory" label={t('Memory (MB)')} rules={[{ required: true, message: t('Required') }]}>
                                            <InputNumber min={256} max={524288} step={256} style={{ width: '100%' }} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={12} md={6}>
                                        <Form.Item name="net_out" label={t('Bandwidth (Mbps)')} rules={[{ required: true, message: t('Required') }]}>
                                            <InputNumber min={1} max={10000} style={{ width: '100%' }} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={12} md={6}>
                                        <Form.Item name="flow_limit" label={t('Monthly traffic (GB)')} tooltip={t('0 means unlimited')} rules={[{ required: true, message: t('Required') }]}>
                                            <InputNumber min={0} style={{ width: '100%' }} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Space>
                                    <Button type="primary" htmlType="submit" icon={<ThunderboltOutlined />} loading={submitting}>{t('Save configuration')}</Button>
                                    <Form.Item name="restart" valuePropName="checked" noStyle>
                                        <Checkbox>{t('Restart now to apply')}</Checkbox>
                                    </Form.Item>
                                </Space>
                            </Form>
                        ),
                    }, {
                        key: 'cpu',
                        label: t('Advanced: CPU model / nested virtualization'),
                        children: <CpuForm id={id} adapter={adapter} reload={reload} disabled={disabled} />,
                    }]}
                />
            )}

            <Modal
                title={titles[modal]}
                open={!!modal}
                onCancel={() => setModal(null)}
                onOk={() => form.submit()}
                confirmLoading={submitting}
                destroyOnClose
            >
                {modal === 'vnc' && <Text type="warning" style={{ display: 'block', marginBottom: 12 }}>{t('The instance will restart after this change')}</Text>}
                <Form form={form} layout="vertical" onFinish={submit}>
                    <Form.Item
                        name="value"
                        label={modal === 'hostname' ? t('Hostname') : t('New password')}
                        rules={[
                            { required: true, message: t('Required') },
                            ...(modal === 'hostname' ? [{ pattern: /^[A-Za-z0-9][A-Za-z0-9.-]{0,62}$/, message: t('Letters, numbers, dots, and hyphens only') }] : []),
                            ...(modal === 'password' ? [{ min: 8, message: t('At least 8 characters') }] : []),
                        ]}
                    >
                        {modal === 'hostname' ? <Input /> : (
                            <Input
                                addonAfter={(
                                    <a onClick={() => form.setFieldsValue({ value: generatePassword(modal === 'vnc' ? 8 : 12) })}>{t('Random')}</a>
                                )}
                            />
                        )}
                    </Form.Item>
                </Form>
            </Modal>
        </Space>
    );
};

export default SettingsTab;
